import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/pickers";
import { StateZoneFields } from "@/components/StateZoneFields";

const NONE = "__none__";
const BLANK = { name: "", email: "", employee_code: "", mobile: "", department_id: "", designation_id: "", location_id: "", date_of_joining: "", state_code: "", zone_id: "" };

function useMasters(enabled) {
  const { actingOrgId } = useAuth();
  return useQuery({
    queryKey: ["ro-masters", actingOrgId], enabled,
    queryFn: async () => {
      const [d, g, l] = await Promise.all(["departments", "designations", "locations"].map((m) => api.get(`/org/masters/${m}`)));
      return { department_id: d.data, designation_id: g.data, location_id: l.data };
    },
  }).data || { department_id: [], designation_id: [], location_id: [] };
}

function SingleForm({ onDone, open, initial, empId }) {
  const masters = useMasters(open);
  const [f, setF] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      if (empId) { await api.put(`/org/employees/${empId}/roster-only`, f); toast.success("Details updated"); }
      else { await api.post("/org/employees/roster-only", f); toast.success(`${f.name} added`); setF(BLANK); }
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {[["name", "Full name *"], ["email", "Email *"], ["employee_code", "Employee code"], ["mobile", "Mobile"]].map(([k, l]) => (
          <div key={k}><Label>{l}</Label><Input className="mt-1" value={f[k]} onChange={upd(k)} data-testid={`ro-${k}`} /></div>
        ))}
        {[["department_id", "Department"], ["designation_id", "Designation"], ["location_id", "Location"]].map(([k, l]) => (
          <div key={k}><Label>{l}</Label>
            <Select value={f[k] || NONE} onValueChange={(v) => setF({ ...f, [k]: v === NONE ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid={`ro-${k}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>Not set</SelectItem>{masters[k].map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select></div>
        ))}
        <div><Label>Date of joining</Label><div className="mt-1"><DatePicker value={f.date_of_joining} onChange={(v) => setF({ ...f, date_of_joining: v })} clearable testid="ro-date_of_joining" /></div></div>
        <StateZoneFields value={f} onChange={(v) => setF({ ...f, ...v })} testPrefix="ro" />
      </div>
      <DialogFooter><Button disabled={!f.name.trim() || !f.email.trim() || busy} onClick={submit} data-testid="ro-submit">{busy ? "Saving…" : empId ? "Save changes" : "Add employee"}</Button></DialogFooter>
    </div>
  );
}

function BulkUpload({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const template = async () => {
    const res = await api.get("/org/employees/roster-only/template", { responseType: "blob" });
    const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "roster_employees_template.xlsx"; a.click(); URL.revokeObjectURL(u);
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { setPreview((await api.post("/org/employees/roster-only/preview", fd)).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/org/employees/roster-only/commit", { rows: preview.rows });
      toast.success(`${data.created} employee(s) added${data.updated ? `, ${data.updated} updated` : ""}${data.created_masters ? `, ${data.created_masters} master(s) created` : ""}`);
      setPreview(null); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={template} data-testid="ro-template"><Download className="mr-1 h-4 w-4" /> Download template</Button>
        <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 text-sm hover:bg-slate-50">
          <Upload className="mr-1 h-4 w-4" /> {busy ? "Reading…" : "Upload .xlsx / .csv"}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="ro-file" />
        </label>
      </div>
      <p className="text-xs text-slate-400">Columns: name, email (required), employee_code, mobile, department, designation, location, date_of_joining, state, zone. Departments, designations, locations and zones that don't exist yet are created automatically. If zone is blank, the zone covering the state is used. Emails already in the system are updated; blank cells keep the current value.</p>
      {preview && (
        <>
          <div className="max-h-72 overflow-auto rounded border border-slate-100" data-testid="ro-preview">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-slate-500"><tr><th className="p-2">Row</th><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Dept / Desig / Location / State / Zone</th><th className="p-2">Status</th></tr></thead>
              <tbody>{preview.rows.map((r) => (
                <tr key={r.row_number} className="border-t border-slate-100" data-testid={`ro-preview-row-${r.row_number}`}>
                  <td className="p-2">{r.row_number}</td><td className="p-2">{r.name}</td><td className="p-2">{r.email}</td>
                  <td className="p-2">{[r.department, r.designation, r.location, r.state, r.zone].filter(Boolean).join(" / ") || "-"}</td>
                  <td className={`p-2 ${r.status === "ok" ? "text-emerald-600" : r.status === "update" ? "text-indigo-700" : "text-rose-600"}`}>{r.status === "ok" ? "Ready" : r.status === "update" ? "Will update" : "Skipped"}{r.reason ? ` — ${r.reason}` : ""}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <DialogFooter><Button disabled={!preview.valid_count || busy} onClick={commit} data-testid="ro-commit">Add {preview.new_count}, update {preview.update_count}</Button></DialogFooter>
        </>
      )}
    </div>
  );
}

export const EditRosterOnlyDialog = ({ emp, open, onClose, onDone }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>Edit employee details</DialogTitle>
        <DialogDescription>Changes apply straight away, including on rosters.</DialogDescription>
      </DialogHeader>
      {open && (
        <SingleForm open={open} empId={emp.id} onDone={() => { onDone(); onClose(); }}
          initial={Object.fromEntries(Object.keys(BLANK).map((k) => [k, emp[k] || ""]))} />
      )}
    </DialogContent>
  </Dialog>
);

export const RosterOnlyDialog = ({ open, onClose, onDone }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>Add roster-only employees</DialogTitle>
        <DialogDescription>For people who won't go through onboarding. They get no invite or joining form and can be rostered right away.</DialogDescription>
      </DialogHeader>
      <Tabs defaultValue="single">
        <TabsList className="mb-3"><TabsTrigger value="single" data-testid="ro-tab-single">Single</TabsTrigger><TabsTrigger value="bulk" data-testid="ro-tab-bulk">Bulk upload</TabsTrigger></TabsList>
        <TabsContent value="single"><SingleForm open={open} onDone={onDone} /></TabsContent>
        <TabsContent value="bulk"><BulkUpload onDone={() => { onDone(); onClose(); }} /></TabsContent>
      </Tabs>
    </DialogContent>
  </Dialog>
);
